import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import ProductCard, { type Product } from '@/components/base/product-card'
import Section from '@/components/base/section'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'

import { productsPushRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type ProductsPushProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    paragraph: string
    link: WPLink
    items: Product[]
  }>

const ProductsPush = ({
  title,
  paragraph,
  link,
  items,
  ...props
}: ProductsPushProps) => {
  return (
    <Motion animation={productsPushRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Container>
            <Grid className="items-center gap-y-spacing-lg">
              <div
                className={cn(
                  'col-span-2 md:col-span-6 lg:order-2 lg:col-span-5 xl:col-span-4 xl:col-start-9',
                  'grid gap-y-spacing-lg px-margin lg:px-0'
                )}
              >
                {title ? (
                  <Typography
                    variant="h2"
                    component="h2"
                    className="font-medium"
                    data-motion-title
                  >
                    {title}
                  </Typography>
                ) : null}

                <div className="grid gap-y-spacing-sm">
                  {paragraph ? (
                    <Typography
                      variant="regular"
                      paragraph
                      data-motion-paragraph
                    >
                      {paragraph}
                    </Typography>
                  ) : null}

                  {link && link.url ? (
                    <div className="col-span-2">
                      <Button
                        isLiquid
                        asChild
                        variant="primary"
                        data-motion-button
                      >
                        <Link
                          href={link.url}
                          target={link.target}
                          scroll={false}
                        >
                          <TextEffect>
                            <Typography variant="regular">
                              {link.title}
                            </Typography>
                          </TextEffect>
                        </Link>
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div
                className={cn(
                  'col-span-2 md:col-span-6 lg:order-1 lg:col-span-6 lg:col-start-2',
                  'grid grid-cols-2 gap-x-gutter gap-y-spacing-xs md:gap-y-gutter'
                )}
              >
                {items
                  ?.filter((item) => item.title !== '')
                  ?.map((item, index) => (
                    <div
                      key={`product-card-${index}`}
                      className="col-span-2 md:col-span-1"
                    >
                      <ProductCard {...item} data-motion-card />
                    </div>
                  ))}
              </div>
            </Grid>
          </Container>
        </Section>
      )}
    </Motion>
  )
}

export default ProductsPush
